import gql from 'graphql-tag';
import PICK_LIST_FRAGMENT_FOR_SERVICE_DOCKET from './PICK_LIST_FRAGMENT_FOR_SERVICE_DOCKET';
import SERVICE_DOCKET_FRAGMENT from './SERVICE_DOCKET_FRAGMENT';

export { default as MODIFY_COLLECTED_VALUES } from './MODIFY_COLLECTED_VALUES';

export const GET_MARKET_RESTOCK_CORRECTION = gql`
	query getMarketRestockCorrection($id: String) {
		getServiceDocket(id: $id) {
			id
			inventoryCheck
			restockRevision
			marketRestockStockReviewAvailability {
				allowed
				reason
				correctionRevision
			}
			serviceRun {
				id
				driver {
					id
				}
			}
			marketRestockCorrectionAvailability {
				allowed
				reason
			}
			restockCorrections {
				revision
				operationId
				correctedAt
				reason
				changes {
					pickListItem
					product
					productName
					selection
					before {
						deliveredQuantity
						planogramItemExpiryDatesAtPick
					}
					after {
						deliveredQuantity
						planogramItemExpiryDatesAtPick
					}
				}
				correctedBy {
					id
					firstname
					lastname
				}
			}
			machine {
				id
				assetId
				type
				isOffline
				place {
					id
					name
				}
			}
			pickList {
				id
				pickListItems {
					id
					selection
					originalRestockDeliveredQuantity
					restockDeliveredQuantity
					swapQuantity
					planogramItemQuantityPostService
					planogramItemQuantityPostInventoryChange
					planogramItemParlevel
					missingProductQuantity
					extraProductQuantity
					spoiledProductQuantity
					removedProductQuantity
					stockReductionClassifications {
						revision
					}
					planogramItemExpiryDatesAtPick
					product {
						id
						name
						imageUrl
						isFreshFood
					}
					removedProduct {
						id
						name
					}
				}
			}
		}
	}
`;

export const CORRECT_MARKET_RESTOCK = gql`
	mutation correctMarketRestock(
		$serviceDocketId: ID!,
		$reason: String!,
		$expectedRevision: Int!,
		$expectedInventoryCheck: String,
		$changes: JSON!,
		$operationId: String!,
		$adminTraining: Boolean,
	) {
		correctMarketRestock(
			serviceDocketId: $serviceDocketId,
			reason: $reason,
			expectedRevision: $expectedRevision,
			expectedInventoryCheck: $expectedInventoryCheck,
			changes: $changes,
			operationId: $operationId,
			adminTraining: $adminTraining,
		) {
			id
			restockRevision
			marketRestockStockReviewAvailability {
				allowed
				reason
				correctionRevision
			}
		}
	}
`;

const PICKING_PLANNER_SERVICE_DOCKET_FRAGMENT = gql`
	fragment pickingPlannerServiceDocket on ServiceDocket {
		id
		planned
		order
		status
		machineServiceType
		inventoryPicked
		inventoryPartiallyPicked
		totalPickItemsQuantity
		pickerWorkDate
		pickerAssignmentSource
		pickerAssignmentReason
		packTags {
			id
			name
			abbreviation
			labelPrintOnly
		}
		picker {
			id
			firstname
			lastname
		}
		serviceRun {
			id
			name
			planned
			route {
				id
				name
			}
			driver {
				id
				firstname
				lastname
			}
		}
		pickList {
			id
			isAnyItemPicked
		}
		machine {
			id
			assetId
			type
			place {
				id
				name
			}
			location {
				id
				name
			}
		}
	}
`;

const PICKER_MY_DAY_SERVICE_DOCKET_FRAGMENT = gql`
	fragment pickerMyDayServiceDocket on ServiceDocket {
		...pickingPlannerServiceDocket
		serviceRun {
			driver {
				username
			}
		}
		pickList {
			hasProductSoldSinceLastPickListRecalculation
			pickers {
				id
				firstname
				lastname
				username
			}
		}
	}
	${PICKING_PLANNER_SERVICE_DOCKET_FRAGMENT}
`;

export const CHANGE_SERVICE_DOCKET_ORDER = gql`
	mutation changeServiceDocketOrder($id: ID!, $newOrder: Int!, $machineOrder: Int) {
		changeServiceDocketOrder(id: $id, newOrder: $newOrder, machineOrder: $machineOrder) {
			id
			order
			serviceRun {
				id
				machineOrder
			}
		}
	}
`;

export const CHANGE_SERVICE_DOCKET_ORDERS = gql`
	mutation changeServiceDocketOrders($orderByDocketId: JSON!, $machineOrder: Int) {
		changeServiceDocketOrders(orderByDocketId: $orderByDocketId, machineOrder: $machineOrder) {
			id
			order
			serviceRun {
				id
				machineOrder
			}
		}
	}
`;

export const ASSIGN_SERVICE_DOCKETS_PICKER = gql`
	mutation assignServiceDocketsPicker($serviceDocketIds: [ID]!, $pickerId: ID, $pickingDate: String) {
		assignServiceDocketsPicker(serviceDocketIds: $serviceDocketIds, pickerId: $pickerId, pickingDate: $pickingDate) {
			id
			pickerWorkDate
			pickerAssignmentSource
			pickerAssignmentReason
			picker {
				id
				firstname
				lastname
			}
		}
	}
`;

export const ADD_SERVICE_DOCKETS_TO_PICKING_DAY = gql`
	mutation addServiceDocketsToPickingDay(
		$serviceDocketIds: [ID]!,
		$pickingDate: String!,
		$allowMove: Boolean,
		$expectedPickingDate: String,
		$expectedPickingDatesByDocketId: JSON,
	) {
		addServiceDocketsToPickingDay(
			serviceDocketIds: $serviceDocketIds,
			pickingDate: $pickingDate,
			allowMove: $allowMove,
			expectedPickingDate: $expectedPickingDate,
			expectedPickingDatesByDocketId: $expectedPickingDatesByDocketId,
		) {
			id
			pickerWorkDate
		}
	}
`;

export const RESTORE_SERVICE_DOCKETS_TO_NORMAL_PICKING_DAY = gql`
	mutation restoreServiceDocketsToNormalPickingDay(
		$serviceDocketIds: [ID]!,
		$pickingDate: String!,
		$expectedRunDatesByDocketId: JSON!,
	) {
		restoreServiceDocketsToNormalPickingDay(
			serviceDocketIds: $serviceDocketIds,
			pickingDate: $pickingDate,
			expectedRunDatesByDocketId: $expectedRunDatesByDocketId,
		) {
			id
			pickerWorkDate
			pickerAssignmentSource
			pickerAssignmentReason
			picker {
				id
			}
		}
	}
`;

export const GET_PICKING_WORK_FOR_DAY = gql`
	query getPickingWorkForDay($date: String!) {
		getPickingWorkForDay(date: $date) {
			...pickerMyDayServiceDocket
		}
	}
	${PICKER_MY_DAY_SERVICE_DOCKET_FRAGMENT}
`;

export const GET_PICKING_PLANNER_WORK_FOR_DAY = gql`
	query getPickingPlannerWorkForDay($date: String!) {
		getPickingWorkForDay(date: $date) {
			...pickingPlannerServiceDocket
			picker {
				username
			}
			serviceRun {
				driver {
					username
				}
			}
			pickList {
				pickers {
					id
					firstname
					lastname
					username
				}
			}
		}
	}
	${PICKING_PLANNER_SERVICE_DOCKET_FRAGMENT}
`;

export const GET_PICKING_PLANNER_AVAILABLE_WORK = gql`
	query getPickingPlannerAvailableWork(
		$date: String!,
		$page: Int,
		$perPage: Int,
		$query: String,
	) {
		getPickingPlannerAvailableWork(
			date: $date,
			page: $page,
			perPage: $perPage,
			query: $query,
		) {
			items {
				...pickingPlannerServiceDocket
			}
			count
		}
	}
	${PICKING_PLANNER_SERVICE_DOCKET_FRAGMENT}
`;

export const GET_PICKING_PLANNER_AVAILABLE_WORK_GROUPS = gql`
	query getPickingPlannerAvailableWorkGroups(
		$date: String!,
		$page: Int,
		$perPage: Int,
		$query: String,
	) {
		getPickingPlannerAvailableWorkGroups(
			date: $date,
			page: $page,
			perPage: $perPage,
			query: $query,
		) {
			items {
				id
				serviceRunId
				scheduledDate
				eligibleCount
				addedCount
				restorableCount
				unavailableCount
				dockets {
					availability
					docket {
						...pickingPlannerServiceDocket
					}
				}
			}
			count
		}
	}
	${PICKING_PLANNER_SERVICE_DOCKET_FRAGMENT}
`;

export const AUTO_ASSIGN_SERVICE_DOCKET_PICKERS = gql`
	mutation autoAssignServiceDocketPickers($date: String!) {
		autoAssignServiceDocketPickers(date: $date) {
			runDate
			pickingDate
			assignedCount
			skippedManualCount
			skippedStartedCount
			pickerCount
		}
	}
`;

export const MARK_SERVICE_DOCKET_PICK_PARTIALLY_COMPLETE = gql`
	mutation markServiceDocketPickPartiallyComplete($serviceDocketId: ID!, $adminTraining: Boolean) {
		markServiceDocketPickPartiallyComplete(serviceDocketId: $serviceDocketId, adminTraining: $adminTraining) {
			id
			inventoryPicked
			inventoryPartiallyPicked
		}
	}
`;

export const GET_SERVICE_DOCKET_FOR_CONFIRM_STOCK_LEVELS = gql`
	query($id: String) {
		getServiceDocket(id: $id) {
			...serviceDocketDetails
			marketRestockStockReviewAvailability {
				allowed
				reason
				correctionRevision
			}
		}
	}
	${SERVICE_DOCKET_FRAGMENT}
`;

export const CONFIRM_STOCK_LEVELS = gql`
	mutation confirmStockLevels (
		$serviceDocketId: ID!,
		$stockChanges: JSON,
		$checkedProductExpirySchedules: [ID],
		$confirmedAt: Date,
		$operationId: String,
		$expectedRestockRevision: Int,
		$restockCorrectionStockReviewRevision: Int,
		$adminTraining: Boolean,
	) {
		confirmStockLevels (
			serviceDocketId: $serviceDocketId,
			stockChanges: $stockChanges,
			checkedProductExpirySchedules: $checkedProductExpirySchedules,
			confirmedAt: $confirmedAt,
			operationId: $operationId,
			expectedRestockRevision: $expectedRestockRevision,
			restockCorrectionStockReviewRevision: $restockCorrectionStockReviewRevision,
			adminTraining: $adminTraining,
		) {
			id
			inventoryCheck
			replacementReviewRequired
			replacementReviewReason

			pickList {
				...pickList
			}
		}
	}${PICK_LIST_FRAGMENT_FOR_SERVICE_DOCKET}
`;

export const CONFIRM_OFFLINE_MACHINE_VALUES = gql`
	mutation confirmOfflineMachineValues (
		$serviceDocketId: ID!,
		$stockChanges: JSON,
		$checkedProductExpirySchedules: [ID],
		$confirmedAt: Date,
		$operationId: String,
		$expectedRestockRevision: Int,
		$adminTraining: Boolean,
	) {
		confirmOfflineMachineValues (
			serviceDocketId: $serviceDocketId,
			stockChanges: $stockChanges,
			checkedProductExpirySchedules: $checkedProductExpirySchedules,
			confirmedAt: $confirmedAt,
			operationId: $operationId,
			expectedRestockRevision: $expectedRestockRevision,
			adminTraining: $adminTraining,
		) {
			id
			inventoryCheck
			replacementReviewRequired
			replacementReviewReason

			pickList {
				...pickList
			}
		}
	}${PICK_LIST_FRAGMENT_FOR_SERVICE_DOCKET}
`;

export const REPORT_INCORRECT_STOCK_SETTING = gql`
	mutation reportIncorrectStockSetting(
		$serviceDocketId: ID!,
		$planogramItemId: ID!,
		$setting: String!,
		$suggestedValue: Int,
		$detail: String,
	) {
		reportIncorrectStockSetting(
			serviceDocketId: $serviceDocketId,
			planogramItemId: $planogramItemId,
			setting: $setting,
			suggestedValue: $suggestedValue,
			detail: $detail,
		) {
			serviceDocketId
			planogramItemId
			setting
			reported
		}
	}
`;
